import React, { useState, useRef, useContext } from 'react';
import { Switch, Route, Link } from 'react-router-dom';
import QRCode from 'react-qr-code';
import Rating from '../../question_components/rating/index.jsx';
import OptionsAndOther from '../../question_components/options_and_other.jsx';
import TextInput from '../../question_components/text_input.jsx';
import ImportantSafetyInformationStickyFooter from '../../../isi/sticky_footer/index.jsx';
import BylvayIsi from '../../../isi/important_safety_information/bylvay/index.jsx';
import Context from '../../../../utils/context.js';

import styles from '../../questionnaire.module.css';

function Bylvay({ path, update, answeredFinalQuestion, completeSurvey, history, conversationSurvey }) {
  const [rating, setRating] = useState('');
  const [comfortLevel, setComfortLevel] = useState('');
  const [confidenceLevel, setConfidenceLevel] = useState('');
  const [connectionWithOthers, setConnectionWithOthers] = useState('');
  const [readyForNextStep, setReadyForNextStep] = useState('');
  const [notReadyReasons, setNotReadyReasons] = useState([]);
  const [recommendProgram, setRecommendProgram] = useState('');
  const [anythingElse, setAnythingElse] = useState('');
  const [platformEase, setPlatformEase] = useState('');
  const [platformImprovements, setPlatformImprovements] = useState([]);
  const [inspiredToShare, setInspiredToShare] = useState('');
  const [interestedInMentoring, setInterestedInMentoring] = useState('');
  const isiRef = useRef(null);
  const context = useContext(Context);

  const hasHadTreatment = conversationSurvey.match_data?.has_had_treatment?.toLowerCase() === 'yes';

  return (
    <Switch>
      {/* 1 - Star rating */}
      <Route exact path={`${path}`}>
        <Rating
          title={`Rate the conversation with your mentor ${conversationSurvey.patient_mentor_name}.`}
          para='Your rating will not be shared directly with your mentor. It helps PatientPartner improve the program experience and support future connections.'
          answers={{ rating: rating }}
          numberRating={true}
          star={true}
          icons={[
            { text: 'Very disappointing' },
            { text: 'Disappointing' },
            { text: 'Somewhat helpful' },
            { text: 'Helpful' },
            { text: 'Very helpful' },
          ]}
          textLabels={['Very disappointing', 'Disappointing', 'Somewhat helpful', 'Helpful', 'Very helpful']}
          keyForAnswer={'rating'}
          onSubmit={(answers) => {
            setRating(answers.rating);
            update({ rating: answers.rating });
            history.push(`${path}/comfort-level`);
          }}
        />
        <ImportantSafetyInformationStickyFooter
          component={<BylvayIsi showFooter />}
          initialHeight={185}
          initialHeightMobile={185}
          expandedHeight={800}
          ref={isiRef}
        />
      </Route>

      {/* 2 - Comfort level */}
      <Route exact path={`${path}/comfort-level`}>
        <OptionsAndOther
          answers={{ comfort_level: comfortLevel }}
          title='My mentor made me (or someone I care for) feel more comfortable taking the next step in my treatment journey.'
          para='Help us understand if your mentor eased your concerns.'
          keyForAnswer={'comfort_level'}
          choices={['Strongly agree', 'Agree', 'Neither agree nor disagree', 'Disagree', 'Strongly disagree']}
          showOther={false}
          selectOne
          onSubmit={(answers) => {
            setComfortLevel(answers.comfort_level);
            update({ data: { comfort_level: answers.comfort_level } });
            history.push(`${path}/confidence-level`);
          }}
          onBack={() => history.push(`${path}`)}
        />

        <ImportantSafetyInformationStickyFooter
          component={<BylvayIsi showFooter />}
          initialHeight={185}
          initialHeightMobile={185}
          expandedHeight={800}
          ref={isiRef}
        />
      </Route>

      {/* 3 - BYLVAY experience helpful */}
      <Route exact path={`${path}/confidence-level`}>
        <OptionsAndOther
          answers={{ confidence_level: confidenceLevel }}
          title='Hearing my mentor&rsquo;s experience with BYLVAY (odevixibat) was helpful.'
          para='Reflecting on your conversation, please indicate how much you agree with the following statement.'
          keyForAnswer={'confidence_level'}
          choices={['Strongly agree', 'Agree', 'Neither agree nor disagree', 'Disagree', 'Strongly disagree']}
          showOther={false}
          selectOne
          onSubmit={(answers) => {
            setConfidenceLevel(answers.confidence_level);
            update({ data: { confidence_level: answers.confidence_level } });
            history.push(`${path}/connection-with-others`);
          }}
          onBack={() => history.push(`${path}/comfort-level`)}
        />

        <ImportantSafetyInformationStickyFooter
          component={<BylvayIsi showFooter />}
          initialHeight={185}
          initialHeightMobile={185}
          expandedHeight={800}
          ref={isiRef}
        />
      </Route>

      {/* 4 - Connection with others */}
      <Route exact path={`${path}/connection-with-others`}>
        <OptionsAndOther
          answers={{ connection_with_others: connectionWithOthers }}
          title='I feel more connected to others managing this condition.'
          para='Help us understand if the program made you feel less alone in your journey.'
          keyForAnswer={'connection_with_others'}
          choices={['True', 'False']}
          showOther={false}
          selectOne
          onSubmit={(answers) => {
            setConnectionWithOthers(answers.connection_with_others);
            update({ data: { connection_with_others: answers.connection_with_others } });
            history.push(`${path}/ready-for-next-step`);
          }}
          onBack={() => history.push(`${path}/confidence-level`)}
        />

        <ImportantSafetyInformationStickyFooter
          component={<BylvayIsi showFooter />}
          initialHeight={185}
          initialHeightMobile={185}
          expandedHeight={800}
          ref={isiRef}
        />
      </Route>

      {/* 5 - Plan to start/continue treatment */}
      <Route exact path={`${path}/ready-for-next-step`}>
        <OptionsAndOther
          answers={{ ready_for_next_step: readyForNextStep }}
          title='Do you (or someone you care for) plan to start or continue treatment with BYLVAY (odevixibat)?'
          para='Help us understand if you feel prepared to take the next step.'
          keyForAnswer={'ready_for_next_step'}
          choices={['Yes', 'Maybe', 'No']}
          showOther={false}
          selectOne
          onSubmit={(answers) => {
            setReadyForNextStep(answers.ready_for_next_step);
            update({
              ready_for_next_step: answers.ready_for_next_step,
            });
            if (answers.ready_for_next_step === 'Yes') {
              history.push(`${path}/recommend-program`);
            } else {
              history.push(`${path}/not-ready-reasons`);
            }
          }}
          onBack={() => history.push(`${path}/connection-with-others`)}
        />

        <ImportantSafetyInformationStickyFooter
          component={<BylvayIsi showFooter />}
          initialHeight={185}
          initialHeightMobile={185}
          expandedHeight={800}
          ref={isiRef}
        />
      </Route>

      {/* 6 - Why not start/continue (conditional: Maybe or No) */}
      <Route exact path={`${path}/not-ready-reasons`}>
        <OptionsAndOther
          answers={{ not_ready_reasons: notReadyReasons }}
          title='Which best describes why you may not start or continue treatment? (Check all that apply)'
          para="We'd love to better understand your situation."
          keyForAnswer={'not_ready_reasons'}
          choices={[
            'I need more time to decide',
            'I still have questions for my doctor',
            'Concerns about possible side effects',
            'Insurance or cost concerns',
            'Waiting on additional testing',
            'Treatment was not recommended by my doctor',
            'Prefer not to say',
          ]}
          showOther={true}
          otherTxt='Other (please describe)'
          selectOne={false}
          onSubmit={(answers) => {
            setNotReadyReasons(answers.not_ready_reasons);
            update({ data: { not_ready_reasons: answers.not_ready_reasons } });
            history.push(`${path}/recommend-program`);
          }}
          onBack={() => history.push(`${path}/ready-for-next-step`)}
        />

        <ImportantSafetyInformationStickyFooter
          component={<BylvayIsi showFooter />}
          initialHeight={185}
          initialHeightMobile={185}
          expandedHeight={800}
          ref={isiRef}
        />
      </Route>

      {/* 7 - Recommend program */}
      <Route exact path={`${path}/recommend-program`}>
        <OptionsAndOther
          answers={{ recommend_program: recommendProgram }}
          title='I would recommend this mentor program to someone with or caring for someone with this condition.'
          para=''
          keyForAnswer={'recommend_program'}
          choices={['Strongly agree', 'Agree', 'Neither agree nor disagree', 'Disagree', 'Strongly disagree']}
          showOther={false}
          selectOne
          onSubmit={(answers) => {
            setRecommendProgram(answers.recommend_program);
            update({ data: { recommend_program: answers.recommend_program } });
            history.push(`${path}/anything-else`);
          }}
          onBack={() => {
            if (readyForNextStep === 'Yes') {
              history.push(`${path}/ready-for-next-step`);
            } else {
              history.push(`${path}/not-ready-reasons`);
            }
          }}
        />

        <ImportantSafetyInformationStickyFooter
          component={<BylvayIsi showFooter />}
          initialHeight={185}
          initialHeightMobile={185}
          expandedHeight={800}
          ref={isiRef}
        />
      </Route>

      {/* 8 - Thank you note (optional) */}
      <Route exact path={`${path}/anything-else`}>
        <TextInput
          answers={{ anything_else: anythingElse }}
          title='Would you like to share a note of thanks with your mentor? (Optional)'
          para='Your message may be shared anonymously with your mentor. Please do not include medical details or treatment-related information'
          keyForAnswer={'anything_else'}
          placeholder='Share appreciation, encouragement, or what stood out.'
          placeholderFontSize={`12px`}
          optional
          onSubmit={(answers) => {
            setAnythingElse(answers.anything_else);
            update({ data: { anything_else: answers.anything_else } });
            history.push(`${path}/platform-ease`);
          }}
          onBack={() => history.push(`${path}/recommend-program`)}
        />

        <ImportantSafetyInformationStickyFooter
          component={<BylvayIsi showFooter />}
          initialHeight={185}
          initialHeightMobile={185}
          expandedHeight={800}
          ref={isiRef}
        />
      </Route>

      {/* 9 - Platform ease of use */}
      <Route exact path={`${path}/platform-ease`}>
        <OptionsAndOther
          answers={{ platform_ease: platformEase }}
          title='How easy was it to use the PatientPartner platform?'
          para='Help us improve the experience for future families.'
          keyForAnswer={'platform_ease'}
          choices={['Very easy', 'Easy', 'Neutral', 'Difficult', 'Very Difficult']}
          showOther={false}
          selectOne
          onSubmit={(answers) => {
            setPlatformEase(answers.platform_ease);
            update({ data: { platform_ease: answers.platform_ease } });
            if (answers.platform_ease === 'Very easy' || answers.platform_ease === 'Easy') {
              history.push(`${path}/inspired-to-share`);
            } else {
              history.push(`${path}/platform-improvements`);
            }
          }}
          onBack={() => history.push(`${path}/anything-else`)}
        />

        <ImportantSafetyInformationStickyFooter
          component={<BylvayIsi showFooter />}
          initialHeight={185}
          initialHeightMobile={185}
          expandedHeight={800}
          ref={isiRef}
        />
      </Route>

      {/* 10 - Platform improvements (conditional: Neutral/Difficult/Very Difficult) */}
      <Route exact path={`${path}/platform-improvements`}>
        <OptionsAndOther
          answers={{ platform_improvements: platformImprovements }}
          title='Is there anything we could improve about the platform experience? (Select all that apply)'
          para=''
          keyForAnswer={'platform_improvements'}
          choices={[
            'The platform was difficult to use',
            'I had trouble accessing or logging in',
            "Something didn't work as expected",
            'It was hard to find what I needed',
            "Pages were slow to load or didn't load properly",
            "The experience didn't work well on my device",
            'I received too many email or text notifications',
            'Getting started or signing up was difficult',
          ]}
          showOther={false}
          selectOne={false}
          onSubmit={(answers) => {
            setPlatformImprovements(answers.platform_improvements);
            update({ data: { platform_improvements: answers.platform_improvements } });
            history.push(`${path}/inspired-to-share`);
          }}
          onBack={() => history.push(`${path}/platform-ease`)}
        />

        <ImportantSafetyInformationStickyFooter
          component={<BylvayIsi showFooter />}
          initialHeight={185}
          initialHeightMobile={185}
          expandedHeight={800}
          ref={isiRef}
        />
      </Route>

      {/* 11 - Inspired to share */}
      <Route exact path={`${path}/inspired-to-share`}>
        <OptionsAndOther
          answers={{ inspired_to_share: inspiredToShare }}
          title='As a result of this interaction, I am inspired to share my story with others navigating this condition.'
          para='Sharing your experience is completely voluntary.'
          keyForAnswer={'inspired_to_share'}
          choices={['Strongly agree', 'Agree', 'Neither agree nor disagree', 'Disagree', 'Strongly disagree']}
          showOther={false}
          selectOne
          onSubmit={(answers) => {
            setInspiredToShare(answers.inspired_to_share);
            update({ data: { inspired_to_share: answers.inspired_to_share } });
            const isAgreed = answers.inspired_to_share === 'Strongly agree' || answers.inspired_to_share === 'Agree';
            if (hasHadTreatment && isAgreed) {
              history.push(`${path}/interested-in-mentoring`);
            } else {
              answeredFinalQuestion({ inspired_to_share: answers.inspired_to_share });
            }
          }}
          onBack={() => {
            if (platformEase === 'Very easy' || platformEase === 'Easy') {
              history.push(`${path}/platform-ease`);
            } else {
              history.push(`${path}/platform-improvements`);
            }
          }}
        />

        <ImportantSafetyInformationStickyFooter
          component={<BylvayIsi showFooter />}
          initialHeight={185}
          initialHeightMobile={185}
          expandedHeight={800}
          ref={isiRef}
        />
      </Route>

      {/* 12 - Interested in becoming a mentor (conditional) */}
      <Route exact path={`${path}/interested-in-mentoring`}>
        <OptionsAndOther
          answers={{ interested_in_mentoring: interestedInMentoring }}
          title='Would you be interested in becoming a PatientPartner Mentor to support others navigating this condition?'
          para="You don't need to be an expert, just someone willing to share your personal experience in a supportive and respectful way."
          keyForAnswer={'interested_in_mentoring'}
          choices={["Yes, I'd like to learn more", 'Maybe in the future', 'No, not at this time']}
          showOther={false}
          selectOne
          onSubmit={(answers) => {
            setInterestedInMentoring(answers.interested_in_mentoring);
            if (answers.interested_in_mentoring === "Yes, I'd like to learn more") {
              completeSurvey({ data: { interested_in_mentoring: answers.interested_in_mentoring } });
              history.push(`${path}/thank-you-mentoring`);
            } else {
              answeredFinalQuestion({ interested_in_mentoring: answers.interested_in_mentoring });
            }
          }}
          onBack={() => history.push(`${path}/inspired-to-share`)}
        />

        <ImportantSafetyInformationStickyFooter
          component={<BylvayIsi showFooter />}
          initialHeight={185}
          initialHeightMobile={185}
          expandedHeight={800}
          ref={isiRef}
        />
      </Route>

      {/* 13 - Thank you for interest in mentoring (QR code screen) */}
      <Route exact path={`${path}/thank-you-mentoring`}>
        <div className='row'>
          <div className='col-sm-2'></div>
          <div className='col-sm-8'>
            <p className={styles.thank_you}>Thank You for Your Interest in Supporting Others!</p>
            <p className={styles.thank_you_p}>
              Your willingness to share your experience can make a meaningful difference for families navigating this
              condition.
            </p>
            <p className={styles.thank_you_p}>
              To learn more about becoming a PatientPartner Mentor,{' '}
              <strong>
                scan the QR code below or click this{' '}
                <a style={{ color: 'blue' }} href='mailto:patient.ambassador@ipsen.com'>
                  link
                </a>
                .
              </strong>
            </p>
            <div style={{ margin: '30px 0' }}>
              <QRCode value='mailto:patient.ambassador@ipsen.com' size={200} />
            </div>
            <Link to={context.auth.isLoggedIn() ? '/member/conversations' : `/member/login`}>
              <button className={`${styles.thank_you_button}`}>Home</button>
            </Link>
          </div>
          <div className='col-sm-2'></div>
        </div>
      </Route>
    </Switch>
  );
}

export default Bylvay;
